import type { Category, CategoryGroup } from '@fd/schemas';

// The owner's order of categories, set on Budgets and used everywhere a category is listed (the
// owner: "ta kolejność by występowała wszędzie"). A main category — a group, or a category without
// one — has its place among the main ones; a group's categories have theirs inside it. Both are
// `sortOrder`; equal ones fall back to the name.

type Sortable = { id: string; sortOrder: number };

type OrderedMain<C extends Sortable, G extends Sortable> =
  | { key: string; group: G; category: null; members: C[] }
  | { key: string; group: null; category: C; members: [] };

export function orderCategories<C extends Sortable & Pick<Category, 'groupId'>, G extends Sortable & Pick<CategoryGroup, 'id'>>(
  categories: readonly C[],
  groups: readonly G[],
  nameOf: (item: C | G) => string,
  locale: string,
): OrderedMain<C, G>[] {
  const by = (a: C | G, b: C | G) => a.sortOrder - b.sortOrder || nameOf(a).localeCompare(nameOf(b), locale) || (a.id < b.id ? -1 : 1);
  const live = new Set(groups.map((g) => g.id));
  const mains: OrderedMain<C, G>[] = [
    ...groups.map((group) => ({ key: `g:${group.id}`, group, category: null, members: categories.filter((c) => c.groupId === group.id).sort(by) })),
    ...categories.filter((c) => !c.groupId || !live.has(c.groupId)).map((category) => ({ key: category.id, group: null, category, members: [] as [] })),
  ];
  // A generic union does not narrow on `group`; one of the two is always there.
  const entity = (m: OrderedMain<C, G>): C | G => (m.group ?? m.category)!;
  return mains.sort((a, b) => by(entity(a), entity(b)));
}

/** Where each category and group stands, for sorting other lists (flow, pickers) the same way. */
export function categoryRanks<C extends Sortable & Pick<Category, 'groupId'>, G extends Sortable & Pick<CategoryGroup, 'id'>>(mains: readonly OrderedMain<C, G>[]) {
  const main = new Map<string, number>();
  const inGroup = new Map<string, number>();
  mains.forEach((m, i) => {
    if (m.group !== null) main.set(`g:${m.group.id}`, i);
    else main.set(m.category!.id, i);
    m.members.forEach((c, j) => inGroup.set(c.id, j));
  });
  return { main, inGroup };
}
